import { useEffect, useState } from "react";
import { MessageCircle, FileText, BookOpen, Download, Sparkles, Loader2, CalendarDays } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { Reveal } from "./anim";
import { CONTACT, SINCE, IMAGES, breadcrumb } from "./marketingData";
import { FESTIVALS, getActiveFestival, getUpcomingFestival } from "./festivals";
import { downloadWhatsApp, downloadFlyer, downloadBrochure, downloadBooklet, TAGLINE, WEBSITE } from "./mediaAssets";
import { toast } from "sonner";

const ASSETS = [
  { key: "whatsapp", label: "WhatsApp image", icon: MessageCircle, run: downloadWhatsApp },
  { key: "flyer", label: "Flyer (A4 PDF)", icon: FileText, run: downloadFlyer },
  { key: "brochure", label: "Brochure (A4 PDF)", icon: BookOpen, run: downloadBrochure },
];

function DownloadButtons({ festival, size = "sm" }) {
  const [busy, setBusy] = useState("");
  const go = async (a) => {
    setBusy(a.key);
    try { await a.run(festival); toast.success(`${a.label} ready — check your downloads`); }
    catch (e) { toast.error("Could not generate that asset. Please try again."); }
    finally { setBusy(""); }
  };
  const pad = size === "lg" ? "px-4 py-2.5 text-sm" : "px-3 py-2 text-xs";
  return (
    <div className="flex flex-wrap gap-2" data-testid={`downloads-${festival.key}`}>
      {ASSETS.map((a) => {
        const Icon = a.icon;
        return (
          <button key={a.key} onClick={() => go(a)} disabled={!!busy}
            data-testid={`download-${a.key}-${festival.key}`}
            className={`inline-flex items-center gap-2 rounded-full font-semibold border border-white/15 bg-white/5 hover:bg-white/12 text-white transition-colors disabled:opacity-50 ${pad}`}>
            {busy === a.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

export default function MediaPage() {
  const [featured, setFeatured] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const active = getActiveFestival(params.get("festival") || params.get("fest"));
    if (active) { setFeatured(active); setIsLive(true); return; }
    const up = getUpcomingFestival(60);
    if (up) { setFeatured(up.festival); setIsLive(false); }
  }, []);

  const [bookletBusy, setBookletBusy] = useState(false);
  const getBooklet = async () => {
    setBookletBusy(true);
    try { await downloadBooklet(); toast.success("Company booklet ready — check your downloads"); }
    catch { toast.error("Could not generate the booklet. Please try again."); }
    finally { setBookletBusy(false); }
  };

  return (
    <div className="bg-[#1A1A18] text-white font-body antialiased overflow-x-hidden" data-testid="media-page">
      <Seo
        title={`Media & Festival Greetings — Downloadable Flyers, Brochures & WhatsApp Images | ITO since ${SINCE}`}
        description="Download ready-to-forward festival greetings, printable flyers, brochures and a full company booklet from Indian Transport Organisation — pan-India transport to every pincode."
        path="/media"
        image={IMAGES.fleetYard}
        imageAlt="Indian Transport Organisation media & greetings"
        keywords="ito festival greetings, transport company brochure, logistics flyer download, whatsapp festival image, indian transport organisation media"
        jsonLd={[breadcrumb([{ name: "Home", path: "/" }, { name: "Media", path: "/media" }])]}
      />
      <MarketingNav heroDark />

      {/* Header */}
      <section className="pt-36 pb-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Media &amp; festive greetings
            </p>
            <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl leading-[1.05]" data-testid="media-title">
              Forward &amp; print ITO greetings to your clients.
            </h1>
            <p className="mt-5 text-white/80 max-w-2xl leading-relaxed">
              Every festival, download a ready-made WhatsApp greeting, a printable flyer and a client brochure —
              plus our full company booklet. {TAGLINE}.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Company booklet CTA */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="rounded-2xl border border-white/12 bg-gradient-to-r from-[#C84B31]/25 to-transparent p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="booklet-card">
            <div>
              <div className="font-heading font-black text-2xl">ITO Company Booklet</div>
              <p className="text-white/75 text-sm mt-1 max-w-xl">A multi-page PDF profile — about us, all services, our full fleet of vehicles and contact — to share with any prospect.</p>
            </div>
            <button onClick={getBooklet} disabled={bookletBusy} data-testid="download-booklet"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#1A1A18] px-6 py-3 font-semibold hover:bg-[#F4F2EC] transition-colors disabled:opacity-60 shrink-0">
              {bookletBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} Download booklet (PDF)
            </button>
          </div>
        </div>
      </section>

      {/* Featured festival */}
      {featured && (
        <section className="pb-12">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="relative rounded-3xl overflow-hidden border border-white/12" data-testid="media-featured" data-festival={featured.key}>
              <img src={featured.image} alt={featured.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0E]/94 via-[#0F0F0E]/70 to-[#0F0F0E]/25" />
              <div className="relative p-7 sm:p-12 max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: featured.accent, color: "#fff" }}>
                  <CalendarDays className="w-3.5 h-3.5" /> {isLive ? "Live today" : "Up next"}
                </span>
                <h2 className="font-heading font-black text-4xl sm:text-5xl mt-4">{featured.name}</h2>
                <p className="mt-4 text-white/85 leading-relaxed">{featured.quote}</p>
                <div className="mt-7"><DownloadButtons festival={featured} size="lg" /></div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All greetings library */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-heading font-black text-2xl sm:text-3xl">The full greeting library</h2>
            <span className="text-white/50 text-sm">{FESTIVALS.length} festivals</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-gallery">
            {FESTIVALS.map((f) => (
              <div key={f.key} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] flex flex-col" data-testid={`media-card-${f.key}`}>
                <div className="relative h-44">
                  <img src={f.image} alt={f.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0E]/90 to-transparent" />
                  <h3 className="absolute bottom-3 left-4 right-4 font-heading font-black text-xl">{f.name}</h3>
                  <span className="absolute top-3 left-3 w-3 h-3 rounded-full" style={{ backgroundColor: f.accent }} />
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <p className="text-white/70 text-xs leading-relaxed line-clamp-3">{f.quote}</p>
                  <div className="mt-auto"><DownloadButtons festival={f} /></div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-white/50 text-sm">
            Questions? Call <a href={CONTACT.phoneHref} className="text-[#D4A373] hover:text-white">+91 {CONTACT.phone}</a> · {WEBSITE}
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
